import { useMemo, useState } from "react";
import type { Project } from "../data/portfolio";
import { summaries } from "../data/summaries";
import ProjectCard from "./ProjectCard";

type ProjectSectionProps = {
  projects: Project[];
};

const ProjectSection = ({ projects }: ProjectSectionProps) => {
  const [query, setQuery] = useState("");
  const [language, setLanguage] = useState("all");
  const [category, setCategory] = useState("all");
  const [context, setContext] = useState("all");

  const languageOptions = useMemo(() => {
    const set = new Set<string>();
    projects.forEach((p) => p.tags.languages.forEach((lang) => set.add(lang)));
    return Array.from(set).sort();
  }, [projects]);

  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    projects.forEach((p) => p.tags.categories.forEach((cat) => set.add(cat)));
    return Array.from(set).sort();
  }, [projects]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects.filter((project) => {
      const summaryText = summaries[project.summaryKey]?.toLowerCase() ?? "";
      const matchesQuery =
        q.length === 0 ||
        project.title.toLowerCase().includes(q) ||
        summaryText.includes(q) ||
        project.impact.toLowerCase().includes(q);
      const matchesLang = language === "all" || project.tags.languages.includes(language);
      const matchesCat = category === "all" || project.tags.categories.includes(category);
      const matchesContext = context === "all" || project.tags.context === context;
      return matchesQuery && matchesLang && matchesCat && matchesContext;
    });
  }, [category, context, language, projects, query]);

  return (
    <section id="projects" className="section">
      <div className="section-header">
        <p className="eyebrow">Selected Work</p>
        <h2>Graphics and vision projects</h2>
        <p className="lede">
          A mix of interactive graphics, visualization tools, and systems work. Each project includes media and links so
          you can quickly evaluate technical depth.
        </p>
      </div>
      <div className="filter-bar">
        <input
          type="search"
          placeholder="Search by title or description"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="filter-controls">
          <label>
            Language
            <select value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value="all">All</option>
              {languageOptions.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
          </label>
          <label>
            Category
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="all">All</option>
              {categoryOptions.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </label>
          <label>
            Context
            <select value={context} onChange={(e) => setContext(e.target.value)}>
              <option value="all">All</option>
              <option value="research">Research</option>
              <option value="personal">Personal</option>
              <option value="class">Class</option>
            </select>
          </label>
        </div>
      </div>
      <div className="project-grid">
        {filtered.map((project) => (
          <ProjectCard key={project.title} project={project} />
        ))}
        {filtered.length === 0 && <p className="meta">No projects match those filters yet.</p>}
      </div>
    </section>
  );
};

export default ProjectSection;
